"use client";

import * as React from "react";
import Link from "next/link";
import { Download, ExternalLink, Lock, Maximize2, X, Sparkles, Copy, Check } from "lucide-react";
import { getResourceCategory } from "@/lib/resource-categories";
import { cn } from "@/lib/cn";
import type { ResourceDTO } from "@/types/resource";
import { AdOverlayDialog, type OverlayAction } from "@/features/ad-overlays/ad-overlay-dialog";

const REGISTER_HREF = "/dang-ky?next=/kho-tai-nguyen";

/** Ảnh xem trước (nếu có) hoặc mockup dựng sẵn theo nhóm. */
function Preview({ resource, contain, className }: { resource: ResourceDTO; contain?: boolean; className?: string }) {
  const cat = getResourceCategory(resource.category);
  const Icon = cat?.icon;
  if (resource.previewUrl) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={resource.previewUrl} alt={resource.title} className={cn("size-full", contain ? "object-contain" : "object-cover", className)} />;
  }
  return (
    <div className={cn("grid size-full place-items-center bg-gradient-to-br p-4 text-center", cat?.grad, className)}>
      <div className="flex flex-col items-center gap-2 rounded-xl bg-white/90 px-4 py-5 shadow-sm">
        {Icon && <Icon className="size-7 text-slate-500" aria-hidden="true" />}
        <span className="line-clamp-2 text-body-sm font-semibold text-slate-700">{resource.title}</span>
      </div>
    </div>
  );
}

/** Nút tải/xem — gate đăng nhập: chưa đăng nhập thì đưa về trang đăng ký.
 *  Nếu tài nguyên gắn overlay đang bật → mở lớp phủ quảng cáo trước khi tới link. */
function Actions({ resource, isLoggedIn, size = "sm" }: { resource: ResourceDTO; isLoggedIn: boolean; size?: "sm" | "md" }) {
  const [action, setAction] = React.useState<OverlayAction | null>(null);
  const overlay = resource.overlay; // chỉ có khi overlay đang bật
  const iconSize = size === "sm" ? "size-3.5" : "size-4";
  const btn = cn(
    "inline-flex items-center justify-center gap-1.5 rounded-full font-semibold transition-colors",
    size === "sm" ? "px-3 py-1.5 text-caption" : "px-4 py-2 text-body-sm"
  );
  const primaryCls = cn(btn, "bg-primary text-white hover:bg-primary-hover");
  const secondaryCls = cn(btn, "border border-border-strong text-foreground hover:bg-subtle");

  const downloadHref = `/api/resources/${resource.id}/download`;

  return (
    <div className="flex flex-wrap gap-2">
      {resource.hasFile &&
        (!isLoggedIn ? (
          <Link href={REGISTER_HREF} title="Đăng ký để tải" className={primaryCls}>
            <Lock className={iconSize} aria-hidden="true" /> Tải xuống
          </Link>
        ) : overlay ? (
          <button type="button" onClick={() => setAction({ type: "download", href: downloadHref })} className={primaryCls}>
            <Download className={iconSize} aria-hidden="true" /> Tải xuống
          </button>
        ) : (
          <a href={downloadHref} className={primaryCls}>
            <Download className={iconSize} aria-hidden="true" /> Tải xuống
          </a>
        ))}

      {resource.viewUrl &&
        (!isLoggedIn ? (
          <Link href={REGISTER_HREF} title="Đăng ký để xem" className={secondaryCls}>
            <Lock className={iconSize} aria-hidden="true" /> Xem toàn bộ
          </Link>
        ) : overlay ? (
          <button type="button" onClick={() => setAction({ type: "view", href: resource.viewUrl! })} className={secondaryCls}>
            <ExternalLink className={iconSize} aria-hidden="true" /> Xem toàn bộ
          </button>
        ) : (
          <a href={resource.viewUrl} target="_blank" rel="noreferrer" className={secondaryCls}>
            <ExternalLink className={iconSize} aria-hidden="true" /> Xem toàn bộ
          </a>
        ))}

      {overlay && (
        <AdOverlayDialog
          overlay={overlay}
          action={action}
          open={action !== null}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}

/** Nhóm "Prompt AI": hiện câu prompt + nút mở thẳng công cụ AI (prefill sẵn). */
function PromptBlock({ resource, isLoggedIn }: { resource: ResourceDTO; isLoggedIn: boolean }) {
  const [copied, setCopied] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const targetPath = resource.promptTarget === "text" ? "/tro-ly-ai" : "/tao-anh-ai";
  const useHref = isLoggedIn
    ? `${targetPath}?prompt=${encodeURIComponent(resource.prompt)}`
    : `/dang-nhap?next=${encodeURIComponent(targetPath)}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(resource.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* trình duyệt chặn clipboard */
    }
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-border bg-subtle/60 p-2.5">
        <p className={cn("whitespace-pre-line text-caption text-foreground-secondary", !expanded && "line-clamp-3")}>
          {resource.prompt}
        </p>
        {(resource.prompt.length > 120 || resource.prompt.includes("\n")) && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-1 text-caption font-medium text-primary hover:underline"
          >
            {expanded ? "Thu gọn" : "Xem đầy đủ"}
          </button>
        )}
      </div>
      <div className="flex gap-2">
        <Link
          href={useHref}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-cyan-500 to-indigo-500 px-3 py-2 text-caption font-semibold text-white transition-opacity hover:opacity-90"
        >
          <Sparkles className="size-3.5" aria-hidden="true" />
          Dùng prompt với AI
        </Link>
        <button
          type="button"
          onClick={copy}
          title="Chép prompt"
          aria-label="Chép prompt"
          className="inline-flex shrink-0 items-center justify-center rounded-full border border-border-strong px-3 text-foreground-secondary hover:bg-subtle"
        >
          {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
        </button>
      </div>
    </div>
  );
}

export function ResourceCard({ resource, isLoggedIn }: { resource: ResourceDTO; isLoggedIn: boolean }) {
  const [open, setOpen] = React.useState(false);
  const isPromptAi = resource.category === "prompt-ai";

  return (
    <>
      <div className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-sm transition-all duration-fast ease-out hover:-translate-y-1 hover:shadow-lg">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="relative block aspect-video w-full overflow-hidden bg-subtle focus-visible:outline-none"
          aria-label={`Xem trước ${resource.title}`}
        >
          <span className="block size-full transition-transform duration-normal ease-out group-hover:scale-105">
            <Preview resource={resource} />
          </span>
          <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-caption font-semibold text-slate-800 shadow">
              <Maximize2 className="size-3.5" aria-hidden="true" /> Xem trước
            </span>
          </span>
        </button>

        <div className="flex flex-1 flex-col gap-2 p-3.5">
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-1 text-body-sm font-semibold text-foreground">{resource.title}</h3>
            {resource.description && (
              <p className="mt-0.5 line-clamp-2 text-caption text-muted">{resource.description}</p>
            )}
          </div>
          {isPromptAi ? (
            <PromptBlock resource={resource} isLoggedIn={isLoggedIn} />
          ) : (
            <Actions resource={resource} isLoggedIn={isLoggedIn} />
          )}
        </div>
      </div>

      {/* Lightbox xem lớn */}
      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="flex max-h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-surface shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
              <h3 className="min-w-0 truncate text-body font-semibold text-foreground">{resource.title}</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Đóng"
                className="grid size-8 shrink-0 place-items-center rounded-md text-muted hover:bg-subtle hover:text-foreground"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            </div>
            <div className="grid flex-1 place-items-center overflow-auto bg-subtle/50 p-4">
              <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-border bg-subtle">
                <div className="aspect-video w-full overflow-hidden">
                  <Preview resource={resource} contain />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
              <p className="min-w-0 flex-1 text-caption text-muted">
                {resource.description || "Tài nguyên chia sẻ cho giáo viên."}
                {!isLoggedIn && <span className="ml-1 font-medium text-primary">· Đăng ký để tải/xem.</span>}
              </p>
              <Actions resource={resource} isLoggedIn={isLoggedIn} size="md" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
